import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Types
// ============================================================================

interface FAQ {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface FAQGroup {
  category: string;
  items: FAQ[];
}

// ============================================================================
// Helpers
// ============================================================================

const UNCATEGORIZED = '__uncategorized__';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim();
  return s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined';
}

function groupByCategory(faqs: FAQ[]): FAQGroup[] {
  const map = new Map<string, FAQ[]>();
  const order: string[] = [];

  for (const faq of faqs) {
    const key = hasValue(faq.category) ? faq.category!.trim() : UNCATEGORIZED;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(faq);
  }

  return order.map((category) => ({
    category,
    items: map.get(category) || [],
  }));
}

// ============================================================================
// Accordion item
// ============================================================================

interface FAQItemProps {
  faq: FAQ;
  itemKey: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ faq, itemKey, isOpen, onToggle }: FAQItemProps) {
  const question = hasValue(faq.question) ? faq.question! : '';
  const answerHtml = hasValue(faq.answer) ? faq.answer! : '';

  const panelId = `faq-panel-${itemKey}`;
  const buttonId = `faq-btn-${itemKey}`;

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <button
        type="button"
        id={buttonId}
        className="faq-question"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="faq-question-text">{question}</span>
        <span className="faq-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="faq-answer-wrapper"
        hidden={!isOpen}
      >
        <div
          className="faq-answer"
          dangerouslySetInnerHTML={{ __html: answerHtml }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FAQList() {
  const faqs = useCollectionBinding<FAQ>('faqlist.items', {
    collection: 'faqs',
  });

  const groups = useMemo(() => groupByCategory(faqs || []), [faqs]);
  const hasMultipleCategories =
    groups.length > 1 ||
    (groups.length === 1 && groups[0].category !== UNCATEGORIZED);
  const showCategoryHeadings = groups.length > 1 && hasMultipleCategories;

  // Track open items keyed by `${groupIndex}-${itemIndex}`.
  const [openKey, setOpenKey] = useState<string | null>(null);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const hasFaqs = (faqs?.length || 0) > 0;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up pt-4 pb-12 md:pt-6 md:pb-20 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="faqlist.intro"
          defaultValue=""
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] text-center max-w-2xl mx-auto mb-10 [&_a]:text-primary [&_a]:underline"
        />

        {hasFaqs ? (
          <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
            <div className="faq-groups">
              {groups.map((group, gi) => {
                const label =
                  group.category === UNCATEGORIZED ? '' : group.category;
                return (
                  <div key={`${group.category}-${gi}`} className="faq-group">
                    {showCategoryHeadings && label && (
                      <h3 className="faq-category-heading">{label}</h3>
                    )}
                    <div className="faq-list">
                      {group.items.map((faq, ii) => {
                        const key = `${gi}-${ii}`;
                        return (
                          <FAQItem
                            key={key}
                            faq={faq}
                            itemKey={key}
                            isOpen={openKey === key}
                            onToggle={() =>
                              setOpenKey((prev) => (prev === key ? null : key))
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </EditableCollectionBinding>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">FAQs will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-category-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          text-align: center;
          padding-bottom: 0.5rem;
          margin-bottom: 1rem;
          border-bottom: 2px solid var(--vibe-color-primary);
        }
        @media (min-width: 768px) {
          .faq-category-heading {
            font-size: 1.4rem;
            text-align: left;
          }
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        .faq-item {
          background: white;
          border: 1px solid var(--vibe-color-border, rgba(0, 0, 0, 0.12));
          border-radius: 4px;
          overflow: hidden;
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .faq-item:hover {
          border-color: color-mix(in srgb, var(--vibe-color-primary) 45%, transparent);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
        }
        .faq-item--open {
          border-color: var(--vibe-color-primary);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .faq-question {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1rem 1.25rem;
          background: transparent;
          border: none;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease;
        }
        @media (min-width: 768px) {
          .faq-question {
            font-size: 1.075rem;
            padding: 1.1rem 1.5rem;
          }
        }
        .faq-question:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 6%, transparent);
        }
        .faq-item--open .faq-question {
          color: var(--vibe-color-primary);
        }

        .faq-question-text {
          flex: 1;
          line-height: 1.4;
          letter-spacing: 0.01em;
        }

        .faq-icon {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          color: var(--vibe-color-primary);
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .faq-icon svg {
          width: 100%;
          height: 100%;
        }
        .faq-item--open .faq-icon {
          transform: rotate(180deg);
        }

        .faq-answer-wrapper {
          padding: 0 1.25rem 1.1rem;
        }
        @media (min-width: 768px) {
          .faq-answer-wrapper {
            padding: 0 1.5rem 1.3rem;
          }
        }

        .faq-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-border, rgba(0, 0, 0, 0.12)) 70%, transparent);
          padding-top: 0.9rem;
        }
        .faq-answer p {
          margin: 0 0 0.75rem;
        }
        .faq-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-answer ul,
        .faq-answer ol {
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-answer ul {
          list-style: disc;
        }
        .faq-answer ol {
          list-style: decimal;
        }
        .faq-answer li {
          margin-bottom: 0.3rem;
        }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer a:hover {
          text-decoration: none;
        }
      `}</style>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="faqlist.intro"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '',
      description:
        'Optional short paragraph shown above the list of questions.',
    },
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FAQList;
